/* eslint-disable @sa-nu/no-date */

import { APP_ENV_TO_DATADOG_ENV, type AppEnv } from "@sa-nu/shared-types";

import { datadogBrowserLogger } from "./browser";
import { getCallStack } from "./getCallerStack";

export type Logger = {
  debug(msg: string, ctx?: Record<string, unknown>): void;
  info(msg: string, ctx?: Record<string, unknown>): void;
  warn(msg: string, ctx?: Record<string, unknown>): void;
  /**
   * エラーログを出す。
   * 第一引数に Error あるいはそのサブクラスのインスタンスを渡すと、Datadogのエラー追跡に stack trace などが含まれるようになる。
   */
  error(err: unknown, ctx?: Record<string, unknown>): void;
  error(msg: string, ctx?: Record<string, unknown>): void;
  error(err: unknown, msg: string, ctx?: Record<string, unknown>): void;
  with: (ctx: Record<string, unknown>) => Logger;
};

// 出力先のインターフェース
type LogOutput = {
  write(data: string): void;
};

// デフォルトの出力先（ブラウザ環境対応）
const defaultStdout: LogOutput = {
  write(data: string): void {
    if (typeof process !== "undefined" && process.stdout) {
      process.stdout.write(data + "\n"); // サーバーサイドでは改行を追加
    } else if (typeof console !== "undefined") {
      // eslint-disable-next-line no-console
      console.log(data); // ブラウザでは console.log が自動的に改行
    }
  },
};

const defaultStderr: LogOutput = {
  write(data: string): void {
    if (typeof process !== "undefined" && process.stderr) {
      process.stderr.write(data + "\n"); // サーバーサイドでは改行を追加
    } else if (typeof console !== "undefined") {
      // eslint-disable-next-line no-console
      console.error(data); // ブラウザでは console.error が自動的に改行
    }
  },
};

// コンストラクタの設定オプション
type LoggerOptions = {
  stdout?: LogOutput;
  stderr?: LogOutput;
  context?: Readonly<Record<string, unknown>>;
};

export class SimpleLogger implements Logger {
  private readonly context: Readonly<Record<string, unknown>>;
  private readonly stdout: LogOutput;
  private readonly stderr: LogOutput;

  constructor(options: LoggerOptions = {}) {
    this.context = options.context ?? {};
    this.stdout = options.stdout ?? defaultStdout;
    this.stderr = options.stderr ?? defaultStderr;
  }

  private formatLog(
    level: string,
    msg: string,
    ctx: Readonly<Record<string, unknown>> = {}
  ): { formattedLog: string; isError: boolean } {
    const timestamp = new Date().toISOString();
    const caller = getCallStack(2)[0]; // スタックトレースから呼び出し元を取得

    const logData = {
      level,
      msg,
      ts: timestamp,
      caller,
      ...this.context,
      ...ctx,
    };

    const formattedLog = JSON.stringify(logData);
    const isError = level === "error";

    return { formattedLog, isError };
  }

  private writeLog(formattedLog: string, isError: boolean): void {
    try {
      if (isError) {
        this.stderr.write(formattedLog);
      } else {
        this.stdout.write(formattedLog);
      }
    } catch (err) {
      // ログ出力自体のエラー処理
      // eslint-disable-next-line no-console
      console.error("Failed to write log:", err);
    }
  }

  debug(msg: string, ctx: Readonly<Record<string, unknown>> = {}): void {
    const { formattedLog, isError } = this.formatLog("debug", msg, ctx);
    this.writeLog(formattedLog, isError);
  }

  info(msg: string, ctx: Readonly<Record<string, unknown>> = {}): void {
    const { formattedLog, isError } = this.formatLog("info", msg, ctx);
    this.writeLog(formattedLog, isError);

    // ブラウザの場合はDatadogにも送信
    if (typeof window !== "undefined") {
      datadogBrowserLogger()?.info(msg, { ...this.context, ...ctx });
    }
  }

  warn(msg: string, ctx: Readonly<Record<string, unknown>> = {}): void {
    const { formattedLog, isError } = this.formatLog("warn", msg, ctx);
    this.writeLog(formattedLog, isError);

    // ブラウザの場合はDatadogにも送信
    if (typeof window !== "undefined") {
      datadogBrowserLogger()?.warn(msg, { ...this.context, ...ctx });
    }
  }

  error(
    errOrMsg: unknown,
    ctxOrMsg?: Readonly<Record<string, unknown>> | string,
    ctxOrUndefined?: Readonly<Record<string, unknown>>
  ): void {
    // エラー整形ロジックの移植（現在のwrapPinoLoggerの実装を維持）
    const msg: string =
      typeof errOrMsg === "string"
        ? errOrMsg
        : typeof ctxOrMsg === "string"
          ? ctxOrMsg
          : errOrMsg instanceof Error
            ? errOrMsg.message
            : JSON.stringify(errOrMsg);

    // https://docs.datadoghq.com/logs/error_tracking/backend/?tab=serilog#attributes-for-error-tracking
    const error =
      errOrMsg instanceof Error
        ? {
            message: errOrMsg.message,
            stack: errOrMsg.stack,
            kind: errOrMsg.constructor.name,
          }
        : { message: msg };

    const ctx = {
      ...(typeof ctxOrMsg === "object" ? ctxOrMsg : {}),
      ...(ctxOrUndefined ?? {}),
      ...(error ? { error } : {}),
    };

    const { formattedLog, isError } = this.formatLog("error", msg, ctx);
    this.writeLog(formattedLog, isError);

    // ブラウザの場合はDatadogにも送信
    if (typeof window !== "undefined") {
      datadogBrowserLogger()?.error(msg, { ...this.context, ...ctx });
    }
  }

  with(ctx: Readonly<Record<string, unknown>>): Logger {
    return new SimpleLogger({
      stdout: this.stdout,
      stderr: this.stderr,
      context: { ...this.context, ...ctx },
    });
  }
}

export function createLogger(options: LoggerOptions = {}): Logger {
  const initialContext: Record<string, unknown> = {
    ...(options.context ?? {}),
  };

  // サーバーサイドの場合、環境変数を追加
  if (typeof window === "undefined") {
    // eslint-disable-next-line no-restricted-syntax
    const appEnv = process.env["APP_ENV"] as AppEnv | undefined;
    initialContext["env"] =
      appEnv != null ? (APP_ENV_TO_DATADOG_ENV[appEnv] ?? appEnv) : appEnv;
    // eslint-disable-next-line no-restricted-syntax
    const commitSha = process.env["VERCEL_GIT_COMMIT_SHA"];
    initialContext["commit"] = commitSha;
    // Datadog Unified Service Tagging の予約属性。RUM (layout.tsx) や
    // source map upload (postbuild) と一致させるため 7 文字に揃える。
    initialContext["version"] = commitSha?.slice(0, 7);
  }

  return new SimpleLogger({
    stdout: options.stdout,
    stderr: options.stderr,
    context: initialContext,
  });
}

export const nopLogger: Logger = {
  debug: () => {},
  info: () => {},
  warn: () => {},
  error: () => {},
  with: () => nopLogger,
};

export const logger = createLogger();
